// FDA Compliance Audit Service
// Tracks all actions for complete transparency and regulatory compliance

const AUDIT_LOGS_KEY = 'fda_audit_logs';
const WORKFLOW_STATE_KEY = 'receiving_workflow_state';

export const auditService = {
  // Log an action for FDA compliance
  logAction: (action) => {
    try {
      const auditLogs = JSON.parse(localStorage.getItem(AUDIT_LOGS_KEY) || '[]');
      
      const auditEntry = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        action: action.type,
        entityType: action.entityType, // 'purchase_order', 'inventory_batch', 'quality_certificate'
        entityId: action.entityId,
        userId: action.userId || 'current-user-id',
        userName: action.userName || 'Demo User',
        details: action.details,
        previousState: action.previousState,
        newState: action.newState,
        complianceData: {
          userRole: action.userRole || 'Warehouse Manager',
          location: action.location || 'Main Warehouse',
          ipAddress: action.ipAddress || 'localhost',
          sessionId: action.sessionId || window.sessionStorage.getItem('sessionId') || Date.now().toString()
        }
      };

      auditLogs.push(auditEntry);
      localStorage.setItem(AUDIT_LOGS_KEY, JSON.stringify(auditLogs));
      
      console.log('FDA Audit Log:', auditEntry);
      return auditEntry;
    } catch (error) {
      console.error('Error logging audit action:', error);
      return null;
    }
  },

  // Get audit logs for specific entity
  getEntityAuditLogs: (entityType, entityId) => {
    try {
      const auditLogs = JSON.parse(localStorage.getItem(AUDIT_LOGS_KEY) || '[]');
      return auditLogs.filter(log => 
        log.entityType === entityType && log.entityId === entityId
      ).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    } catch (error) {
      console.error('Error getting audit logs:', error);
      return [];
    }
  },

  // Get all audit logs (for admin review)
  getAllAuditLogs: () => {
    try {
      const auditLogs = JSON.parse(localStorage.getItem(AUDIT_LOGS_KEY) || '[]');
      return auditLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    } catch (error) {
      console.error('Error getting all audit logs:', error);
      return [];
    }
  },

  // Save workflow state (for persistence)
  saveWorkflowState: (workflowId, state) => {
    try {
      const workflowStates = JSON.parse(localStorage.getItem(WORKFLOW_STATE_KEY) || '{}');
      workflowStates[workflowId] = {
        ...state,
        lastUpdated: new Date().toISOString(),
        userId: state.userId || 'current-user-id'
      };
      localStorage.setItem(WORKFLOW_STATE_KEY, JSON.stringify(workflowStates));
      
      // Log state save action
      auditService.logAction({
        type: 'WORKFLOW_STATE_SAVED',
        entityType: 'receiving_workflow',
        entityId: workflowId,
        details: `Workflow state saved at step ${state.currentStep}`,
        newState: state
      });

      return { success: true };
    } catch (error) {
      console.error('Error saving workflow state:', error);
      return { success: false, error: error.message };
    }
  },

  // Load workflow state (for persistence)
  loadWorkflowState: (workflowId) => {
    try {
      const workflowStates = JSON.parse(localStorage.getItem(WORKFLOW_STATE_KEY) || '{}');
      const state = workflowStates[workflowId];
      
      if (state) {
        // Log state load action
        auditService.logAction({
          type: 'WORKFLOW_STATE_LOADED',
          entityType: 'receiving_workflow',
          entityId: workflowId,
          details: `Workflow state loaded from step ${state.currentStep}`,
          previousState: state
        });
      }

      return state || null;
    } catch (error) {
      console.error('Error loading workflow state:', error);
      return null;
    }
  },

  // Clear workflow state (when completed)
  clearWorkflowState: (workflowId) => {
    try {
      const workflowStates = JSON.parse(localStorage.getItem(WORKFLOW_STATE_KEY) || '{}');
      delete workflowStates[workflowId];
      localStorage.setItem(WORKFLOW_STATE_KEY, JSON.stringify(workflowStates));
      
      // Log state clear action
      auditService.logAction({
        type: 'WORKFLOW_COMPLETED',
        entityType: 'receiving_workflow', 
        entityId: workflowId,
        details: 'Workflow completed and state cleared'
      });

      return { success: true };
    } catch (error) {
      console.error('Error clearing workflow state:', error);
      return { success: false, error: error.message };
    }
  },

  // Generate FDA compliance report for a product batch
  generateComplianceReport: (inventoryId) => {
    const logs = auditService.getEntityAuditLogs('inventory_batch', inventoryId);
    
    const report = {
      inventoryId,
      generatedAt: new Date().toISOString(),
      generatedBy: 'current-user-id',
      
      // Receiving phase
      receivedBy: logs.find(l => l.action === 'INVENTORY_RECEIVED')?.userName,
      receivedAt: logs.find(l => l.action === 'INVENTORY_RECEIVED')?.timestamp,
      receivingLocation: logs.find(l => l.action === 'INVENTORY_RECEIVED')?.complianceData?.location,
      
      // Quality control phase
      qcCertificateUploadedBy: logs.find(l => l.action === 'QC_CERTIFICATE_UPLOADED')?.userName,
      qcCertificateUploadedAt: logs.find(l => l.action === 'QC_CERTIFICATE_UPLOADED')?.timestamp,
      qcApprovedBy: logs.find(l => l.action === 'QC_APPROVED')?.userName,
      qcApprovedAt: logs.find(l => l.action === 'QC_APPROVED')?.timestamp,
      
      // Sales phase (when sold)
      soldTo: logs.find(l => l.action === 'INVENTORY_SOLD')?.details?.customer,
      soldAt: logs.find(l => l.action === 'INVENTORY_SOLD')?.timestamp,
      soldBy: logs.find(l => l.action === 'INVENTORY_SOLD')?.userName,
      
      // Fulfillment phase (when shipped)
      packedBy: logs.find(l => l.action === 'ORDER_PACKED')?.userName,
      packedAt: logs.find(l => l.action === 'ORDER_PACKED')?.timestamp,
      shippedBy: logs.find(l => l.action === 'ORDER_SHIPPED')?.userName,
      shippedAt: logs.find(l => l.action === 'ORDER_SHIPPED')?.timestamp,
      trackingNumber: logs.find(l => l.action === 'ORDER_SHIPPED')?.details?.trackingNumber,
      
      // Complete audit chain
      auditChain: logs
    };
    
    return report;
  }
};
